import React, { useState, useEffect } from 'react';
import { auditsApi, usersApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface AuditTrackingProps {
  user: User;
  onCreateNew: () => void;
  highlightAuditId?: number; // ID de auditoría para resaltar y expandir automáticamente
}

interface AuditDetail {
  id: number;
  titulo: string;
  client?: { id: number; nombre: string; };
  branch?: { id: number; nombre: string; };
  operador?: { id: number; nombre: string; username: string; };
  supervisor?: { id: number; nombre: string; username: string; };
  estado: string;
  prioridad: string;
  fechaSolicitada: string;
  fechaAsignacion?: string;
  fechaInicio?: string;
  fechaTerminacion?: string;
  fechaEntrega?: string;
  fechaPausa?: string;
  costoFinal: number;
  gratisParaCliente?: boolean;
  tiempoEstimadoMinutos?: number;
  tiempoRealMinutos?: number;
  observacionesIniciales?: string;
  observacionesOperador?: string;
  observacionesSupervisor?: string;
  observacionesFinales?: string;
  aprobada?: boolean;
  motivoRechazo?: string;
  requiereRevision?: boolean;
  createdAt: string;
  updatedAt: string;
}

const AuditTracking: React.FC<AuditTrackingProps> = ({ user, onCreateNew, highlightAuditId }) => {
  const [audits, setAudits] = useState<AuditDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedAudit, setSelectedAudit] = useState<AuditDetail | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all'); // Por defecto: todas las auditorías
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showReassignModal, setShowReassignModal] = useState<number | null>(null);
  const [showMessageModal, setShowMessageModal] = useState<number | null>(null);
  const [operators, setOperators] = useState<any[]>([]);

  useEffect(() => {
    loadAudits();
    loadOperators();
  }, [dateRangeFilter, customStartDate, customEndDate]); // Recargar cuando cambie el período o fechas

  // Efecto para resaltar auditoría específica cuando se recibe highlightAuditId
  useEffect(() => {
    if (highlightAuditId && audits.length > 0) {
      const auditToHighlight = audits.find(audit => audit.id === highlightAuditId);
      if (auditToHighlight) {
        setSelectedAudit(auditToHighlight);
        // Scroll hacia la auditoría específica
        setTimeout(() => {
          const element = document.getElementById(`audit-${highlightAuditId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [highlightAuditId, audits]);

  const loadAudits = async () => {
    try {
      setLoading(true);
      const params: any = {
        dateRange: dateRangeFilter === 'all' ? 'all' : dateRangeFilter
      };

      // Si es rango personalizado, agregar fechas
      if (dateRangeFilter === 'custom') {
        if (customStartDate) params.startDate = customStartDate;
        if (customEndDate) params.endDate = customEndDate;
      }

      const response = await auditsApi.getAll(params);
      
      if (response.data.success) {
        // Los datos vienen con relaciones incluidas
        const auditsData = response.data.data.map((audit: any) => ({
          ...audit,
          // Asegurar que las relaciones estén disponibles y normalizar nombres
          client: audit.client || audit.Client,
          branch: audit.branch || audit.Branch,
          operador: audit.operador || audit.Operador ? {
            ...(audit.operador || audit.Operador),
            nombre: (audit.operador || audit.Operador).name,
            username: (audit.operador || audit.Operador).username || (audit.operador || audit.Operador).email?.split('@')[0]
          } : null,
          supervisor: audit.supervisor || audit.Supervisor ? {
            ...(audit.supervisor || audit.Supervisor),
            nombre: (audit.supervisor || audit.Supervisor).name,
            username: (audit.supervisor || audit.Supervisor).username || (audit.supervisor || audit.Supervisor).email?.split('@')[0]
          } : null
        }));
        
        setAudits(auditsData);
      }
    } catch (err: any) {
      console.error('❌ Error cargando auditorías:', err);
      console.error('❌ Error response:', err.response);
      
      let errorMessage = 'Error interno del servidor';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError('Error cargando auditorías: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadOperators = async () => {
    try {
      const response = await usersApi.getAll();
      if (response.data.success) {
        const operadorList = response.data.data.filter((user: any) => user.role === 'operador' || user.role === 'supervisor');
        setOperators(operadorList);
      }
    } catch (err: any) {
      console.error('Error cargando operadores:', err);
    }
  };

  const getStatusColor = (estado: string) => {
    const colors = {
      'pendiente': '#ffc107',
      'asignada': '#17a2b8', 
      'en_proceso': '#28a745',
      'pausada': '#fd7e14',
      'terminada': '#007bff',
      'entregada': '#6c757d',
      'facturada': '#198754'
    };
    return colors[estado as keyof typeof colors] || '#6c757d';
  };

  const getStatusIcon = (estado: string) => {
    const icons = {
      'pendiente': '⏳',
      'asignada': '📋',
      'en_proceso': '🔄',
      'pausada': '⏸️',
      'terminada': '✅',
      'entregada': '📦',
      'facturada': '💰'
    };
    return icons[estado as keyof typeof icons] || '❓';
  };

  const getPriorityColor = (prioridad: string) => {
    const colors = {
      'normal': '#28a745',
      'prioritaria': '#ffc107',
      'urgente': '#dc3545'
    };
    return colors[prioridad as keyof typeof colors] || '#6c757d';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Argentina/Buenos_Aires'
    });
  };

  const calculateDuration = (start: string, end: string) => {
    if (!start || !end) return 'N/A';
    const diff = new Date(end).getTime() - new Date(start).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleReturnForCorrection = async (auditId: number, reason: string) => {
    try {
      const response = await auditsApi.devolverOperador(auditId, reason);
      if (response.data.success) {
        alert(`✅ ${response.data.message || `Auditoría #${auditId} devuelta para corrección`}`);
        setSuccess(response.data.message || `Auditoría #${auditId} devuelta para corrección exitosamente`);
        setSelectedAudit(null); // Cerrar detalles
        loadAudits(); // Recargar lista
      }
    } catch (err: any) {
      alert(`❌ Error: ${err.response?.data?.error || err.message}`);
      setError('Error devolviendo auditoría: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleApproveAudit = async (auditId: number) => {
    try {
      const comentarios = prompt(`Comentarios para aprobar auditoría #${auditId} (opcional):`);
      // Si el usuario cancela el prompt, no continuar
      if (comentarios === null) return;
      
      const response = await auditsApi.approve(auditId, true, comentarios || undefined);
      if (response.data.success) {
        setSuccess(`Auditoría #${auditId} aprobada y enviada al cliente exitosamente`);
        setSelectedAudit(null); // Cerrar detalles
        loadAudits(); // Recargar lista
      }
    } catch (err: any) {
      setError('Error aprobando auditoría: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReassignAudit = async (auditId: number, operadorId?: number, mensaje?: string, prioridad?: string) => {
    try {
      const response = await auditsApi.reassignAudit(auditId, operadorId, mensaje, prioridad);
      if (response.data.success) {
        setSuccess(response.data.message);
        setShowReassignModal(null);
        loadAudits();
      }
    } catch (err: any) {
      setError('Error reasignando auditoría: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddMessage = async (auditId: number, mensaje: string, tipo: 'operador' | 'supervisor' | 'general') => {
    try {
      const response = await auditsApi.addMessage(auditId, mensaje, tipo);
      if (response.data.success) {
        setSuccess('Mensaje agregado exitosamente');
        setShowMessageModal(null);
        loadAudits();
      }
    } catch (err: any) {
      setError('Error agregando mensaje: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleGratisParaCliente = async (auditId: number, gratisParaCliente: boolean) => {
    try {
      const motivo = prompt(
        gratisParaCliente 
          ? 'Motivo para marcar como GRATUITA (opcional):'
          : 'Motivo para marcar como PAGA (opcional):'
      );
      
      const response = await auditsApi.toggleGratis(auditId, gratisParaCliente, motivo || undefined);
      if (response.data.success) {
        setSuccess(`Auditoría marcada como ${gratisParaCliente ? 'gratuita' : 'paga'} exitosamente`);
        loadAudits(); // Recargar lista
      }
    } catch (err: any) {
      setError('Error cambiando estado gratuito: ' + (err.response?.data?.error || err.message));
    }
  };

  // Ordenar: VIEJAS SIN TERMINAR primero, luego las terminadas
  const sortedAudits = [...audits].sort((a, b) => {
    // Estados terminados vs no terminados
    const estadosTerminados = ['terminada', 'entregada', 'facturada'];
    const aTerminada = estadosTerminados.includes(a.estado);
    const bTerminada = estadosTerminados.includes(b.estado);
    
    // Si una está terminada y otra no, la NO terminada va primero
    if (aTerminada && !bTerminada) {
      return 1; // b (no terminada) va antes que a (terminada)
    }
    if (!aTerminada && bTerminada) {
      return -1; // a (no terminada) va antes que b (terminada)
    }
    
    // Si ambas tienen el mismo "estado de terminación", ordenar por antigüedad
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    
    if (dateA !== dateB) {
      return dateA - dateB; // Más viejas primero
    }
    
    // Si tienen la misma fecha, ordenar por prioridad (urgente > prioritaria > normal)
    const prioridadOrder = { 'urgente': 3, 'prioritaria': 2, 'normal': 1 };
    const prioridadA = prioridadOrder[a.prioridad as keyof typeof prioridadOrder] || 1;
    const prioridadB = prioridadOrder[b.prioridad as keyof typeof prioridadOrder] || 1;
    
    return prioridadB - prioridadA; // Mayor prioridad primero
  });

  const filteredAudits = sortedAudits.filter(audit => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (audit.titulo && audit.titulo.toLowerCase().includes(searchLower)) ||
      (audit.client?.nombre && audit.client.nombre.toLowerCase().includes(searchLower)) ||
      (audit.branch?.nombre && audit.branch.nombre.toLowerCase().includes(searchLower)) ||
      (audit.operador?.nombre && audit.operador.nombre.toLowerCase().includes(searchLower)) ||
      (audit.supervisor?.nombre && audit.supervisor.nombre.toLowerCase().includes(searchLower)) ||
      audit.id.toString().includes(searchTerm);

    const matchesState = stateFilter === 'all' || audit.estado === stateFilter;

    return matchesSearch && matchesState;
  });

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>⏳ Cargando seguimiento de auditorías...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div className="flex-responsive" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ margin: 0, color: '#495057' }}>
          📋 Auditorías - Seguimiento Completo
        </h2>
        <button
          onClick={onCreateNew}
          className="btn btn-primary"
          style={{
            background: '#28a745',
            border: 'none',
            fontWeight: 'bold'
          }}
        >
          + Crear Nueva Auditoría
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #fcc',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          {error} (click para cerrar)
        </div>
      )}

      {success && (
        <div style={{
          background: '#efe',
          color: '#373',
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #cfc',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          {success} (click para cerrar)
        </div>
      )}

      {/* Filters */}
      <div className="flex-responsive" style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        padding: '1rem',
        background: 'white',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            🔍 Buscar por cliente, operador, título o ID:
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Ej: Roberto, Leo, ID 7..."
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            📅 Período:
          </label>
          <select
            value={dateRangeFilter}
            onChange={(e) => setDateRangeFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              minWidth: '140px'
            }}
          >
            <option value="today">Hoy</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mes</option>
            <option value="lastMonth">Mes Pasado</option>
            <option value="year">Este Año</option>
            <option value="lastYear">Año Pasado</option>
            <option value="custom">Personalizado</option>
            <option value="all">Todas</option>
          </select>
        </div>
        {dateRangeFilter === 'custom' && (
          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Mes/Año:
            </label>
            <input
              type="month"
              value={customStartDate}
              onChange={(e) => {
                const selectedMonth = e.target.value;
                setCustomStartDate(selectedMonth + '-01');
                // Calcular último día del mes
                const [year, month] = selectedMonth.split('-').map(Number);
                const lastDay = new Date(year, month, 0).getDate();
                setCustomEndDate(`${selectedMonth}-${lastDay}`);
              }}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                minWidth: '150px'
              }}
            />
          </div>
        )}
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            🎯 Estado:
          </label>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px'
            }}
          >
            <option value="all">Todos los estados</option>
            <option value="pendiente">⏳ Pendiente</option>
            <option value="asignada">📋 Asignada</option>
            <option value="en_proceso">🔄 En Proceso</option>
            <option value="pausada">⏸️ Pausada</option>
            <option value="terminada">✅ Terminada</option>
            <option value="entregada">📦 Entregada</option>
            <option value="facturada">💰 Facturada</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'end' }}>
          <button
            onClick={loadAudits}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {/* Tarjeta "Todas" */}
        <div 
          onClick={() => setStateFilter('all')}
          style={{
            background: stateFilter === 'all' ? '#1f2937' : '#6b7280',
            color: 'white',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center',
            cursor: 'pointer',
            transform: stateFilter === 'all' ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.2s ease',
            border: stateFilter === 'all' ? '3px solid #fbbf24' : '3px solid transparent',
            boxShadow: stateFilter === 'all' ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            if (stateFilter !== 'all') {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (stateFilter !== 'all') {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }
          }}
        >
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            📊 {audits.length}
          </div>
          <div style={{ fontSize: '0.875rem' }}>
            Todas
          </div>
          {stateFilter === 'all' && (
            <div style={{ 
              fontSize: '0.7rem', 
              marginTop: '0.25rem', 
              opacity: 0.9,
              fontWeight: 'normal'
            }}>
              ✨ Mostrando todas
            </div>
          )}
        </div>

        {['pendiente', 'asignada', 'en_proceso', 'pausada', 'terminada', 'entregada'].map(estado => {
          const count = audits.filter(a => a.estado === estado).length;
          const isActive = stateFilter === estado;
          return (
            <div 
              key={estado} 
              onClick={() => {
                // Si ya está activo, resetear a 'all', sino aplicar el filtro
                setStateFilter(isActive ? 'all' : estado);
              }}
              style={{
                background: isActive ? '#1f2937' : getStatusColor(estado),
                color: 'white',
                padding: '1rem',
                borderRadius: '6px',
                textAlign: 'center',
                cursor: 'pointer',
                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.2s ease',
                border: isActive ? '3px solid #fbbf24' : '3px solid transparent',
                boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }
              }}
            >
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                {getStatusIcon(estado)} {count}
              </div>
              <div style={{ fontSize: '0.875rem', textTransform: 'capitalize' }}>
                {estado.replace('_', ' ')}
              </div>
              {isActive && (
                <div style={{ 
                  fontSize: '0.7rem', 
                  marginTop: '0.25rem', 
                  opacity: 0.9,
                  fontWeight: 'normal'
                }}>
                  ✨ Filtrado activo
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Audits Table */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#f9fafb',
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          fontWeight: 'bold'
        }}>
          📊 Total: {filteredAudits.length} auditorías encontradas
        </div>

        {filteredAudits.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            📭 No se encontraron auditorías con los filtros aplicados
          </div>
        ) : (
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {filteredAudits.map((audit) => (
              <div
                key={audit.id}
                id={`audit-${audit.id}`}
                style={{
                  padding: '1rem',
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: highlightAuditId === audit.id ? '#fef3c7' : 'white',
                  border: highlightAuditId === audit.id ? '2px solid #f59e0b' : 'none',
                  borderRadius: highlightAuditId === audit.id ? '0.5rem' : '0',
                  marginBottom: highlightAuditId === audit.id ? '0.5rem' : '0'
                }}
                onMouseEnter={(e) => {
                  if (highlightAuditId !== audit.id) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = highlightAuditId === audit.id ? '#fef3c7' : 'white';
                }}
                onClick={() => setSelectedAudit(selectedAudit?.id === audit.id ? null : audit)}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '1.1rem' }}>
                      #{audit.id} - {audit.titulo}
                    </strong>
                    <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      🏢 {audit.client?.nombre} - {audit.branch?.nombre}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{
                      background: getStatusColor(audit.estado),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {getStatusIcon(audit.estado)} {audit.estado.toUpperCase()}
                    </span>
                    <span style={{
                      background: getPriorityColor(audit.prioridad),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {audit.prioridad.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Quick Info */}
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginTop: '1rem',
                  gap: '1rem'
                }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem', 
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    flex: 1
                  }}>
                    <div>
                      👷 <strong>Operador:</strong> 
                      <span style={{ 
                        color: audit.operador?.nombre ? '#059669' : '#dc2626',
                        fontWeight: audit.operador?.nombre ? 'bold' : 'normal'
                      }}>
                        {audit.operador?.nombre || 'Sin asignar'} 
                        {audit.operador?.username && `(@${audit.operador.username})`}
                      </span>
                    </div>
                    <div>
                      👨‍💼 <strong>Supervisor:</strong> 
                      <span style={{ 
                        color: audit.supervisor?.nombre ? '#059669' : '#dc2626',
                        fontWeight: audit.supervisor?.nombre ? 'bold' : 'normal'
                      }}>
                        {audit.supervisor?.nombre || 'Sin asignar'}
                        {audit.supervisor?.username && `(@${audit.supervisor.username})`}
                      </span>
                    </div>
                    {(user.role === 'admin' || user.role === 'cliente') && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        💰 <strong>Costo:</strong> 
                        {audit.gratisParaCliente ? (
                          <span style={{ color: '#4caf50', fontWeight: 'bold' }}>GRATUITA</span>
                        ) : (
                          <span>${audit.costoFinal || 0}</span>
                        )}
                      </div>
                    )}
                    {user.role === 'admin' && audit.horasEstimadas && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        ⏱️ <strong>Tiempo Estimado:</strong>
                        <span>{Math.floor(audit.horasEstimadas)}h {Math.round((audit.horasEstimadas % 1) * 60)}m</span>
                      </div>
                    )}
                    {(user.role === 'admin' || user.role === 'cliente') && (
                      <div>
                        {user.role === 'admin' && (
                        <button
                          onClick={() => toggleGratisParaCliente(audit.id, !audit.gratisParaCliente)}
                          style={{
                            background: audit.gratisParaCliente ? '#ff9800' : '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            marginLeft: '0.5rem'
                          }}
                          title={`Hacer ${audit.gratisParaCliente ? 'paga' : 'gratuita'}`}
                        >
                          {audit.gratisParaCliente ? '💳' : '🆓'}
                        </button>
                        )}
                      </div>
                    )}
                    <div>
                      🎯 <strong>Prioridad:</strong> 
                      <span style={{
                        color: getPriorityColor(audit.prioridad),
                        fontWeight: 'bold'
                      }}>
                        {audit.prioridad.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons - Visible always */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {user.role === 'admin' && audit.estado === 'terminada' && !audit.aprobada && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const reason = prompt(`Motivo para devolver auditoría #${audit.id}:`);
                            if (reason && reason.trim()) {
                              handleReturnForCorrection(audit.id, reason.trim());
                            }
                          }}
                          style={{
                            background: '#ffc107',
                            color: '#000',
                            border: 'none',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            minWidth: '120px'
                          }}
                        >
                          ↩️ Devolver
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApproveAudit(audit.id);
                          }}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            minWidth: '120px'
                          }}
                        >
                          ✅ Aprobar
                        </button>
                      </>
                    )}
                    
                    {user.role === 'admin' && ['pendiente', 'asignada', 'pausada'].includes(audit.estado) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowReassignModal(audit.id);
                        }}
                        style={{
                          background: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          padding: '0.4rem 0.8rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          minWidth: '120px'
                        }}
                      >
                        🔄 Reasignar
                      </button>
                    )}
                    
                    {user.role === 'admin' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMessageModal(audit.id);
                        }}
                        style={{
                          background: '#fd7e14',
                          color: 'white',
                          border: 'none',
                          padding: '0.4rem 0.8rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          minWidth: '120px'
                        }}
                      >
                        💬 Mensaje
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAudit(selectedAudit?.id === audit.id ? null : audit);
                      }}
                      style={{
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        minWidth: '120px'
                      }}
                    >
                      {selectedAudit?.id === audit.id ? '🔼 Cerrar' : '🔽 Ver más'}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedAudit?.id === audit.id && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <h4 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
                      🔍 Flujo Completo - Auditoría #{audit.id}
                    </h4>

                    {/* Timeline */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h5 style={{ marginBottom: '0.75rem' }}>📈 Timeline:</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ color: audit.createdAt ? '#28a745' : '#6c757d' }}>
                          ✅ <strong>Creada:</strong> {formatDate(audit.createdAt)}
                        </div>
                        <div style={{ color: audit.fechaAsignacion ? '#28a745' : '#6c757d' }}>
                          📋 <strong>Asignada:</strong> {formatDate(audit.fechaAsignacion)}
                          {audit.fechaAsignacion && audit.createdAt && (
                            <span style={{ color: '#6b7280', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                              ({calculateDuration(audit.createdAt, audit.fechaAsignacion)})
                            </span>
                          )}
                        </div>
                        <div style={{ color: audit.fechaInicio ? '#28a745' : '#6c757d' }}>
                          🚀 <strong>Iniciada:</strong> {formatDate(audit.fechaInicio)}
                          {audit.fechaInicio && audit.fechaAsignacion && (
                            <span style={{ color: '#6b7280', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                              ({calculateDuration(audit.fechaAsignacion, audit.fechaInicio)})
                            </span>
                          )}
                        </div>
                        {audit.fechaPausa && (
                          <div style={{ color: '#ffc107' }}>
                            ⏸️ <strong>Pausada:</strong> {formatDate(audit.fechaPausa)}
                            {audit.razonPausa && (
                              <div style={{ marginLeft: '1.5rem', fontSize: '0.9rem', fontStyle: 'italic', marginTop: '0.25rem' }}>
                                💬 Motivo: {audit.razonPausa}
                              </div>
                            )}
                          </div>
                        )}
                        <div style={{ color: audit.fechaTerminacion ? '#28a745' : '#6c757d' }}>
                          ✅ <strong>Terminada:</strong> {formatDate(audit.fechaTerminacion)}
                          {audit.fechaTerminacion && audit.fechaInicio && (
                            <span style={{ color: '#6b7280', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                              ({calculateDuration(audit.fechaInicio, audit.fechaTerminacion)})
                            </span>
                          )}
                        </div>
                        <div style={{ color: audit.fechaEntrega ? '#28a745' : '#6c757d' }}>
                          📦 <strong>Entregada:</strong> {formatDate(audit.fechaEntrega)}
                        </div>
                      </div>
                    </div>

                    {/* Observations */}
                    {(audit.observacionesIniciales || audit.observacionesOperador || audit.observacionesSupervisor || audit.observacionesFinales) && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.75rem' }}>💭 Observaciones:</h5>
                        {audit.observacionesIniciales && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>📝 Iniciales:</strong> {audit.observacionesIniciales}
                          </div>
                        )}
                        {audit.observacionesOperador && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>👷 Operador:</strong> {audit.observacionesOperador}
                          </div>
                        )}
                        {audit.observacionesSupervisor && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>👨‍💼 Supervisor:</strong> {audit.observacionesSupervisor}
                          </div>
                        )}
                        {audit.observacionesFinales && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>🏁 Finales:</strong> {audit.observacionesFinales}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quality Control */}
                    {(audit.aprobada !== undefined || audit.requiereRevision || audit.motivoRechazo) && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h5 style={{ marginBottom: '0.75rem' }}>🔍 Control de Calidad:</h5>
                        {audit.aprobada !== undefined && (
                          <div style={{ color: audit.aprobada ? '#28a745' : '#dc3545', marginBottom: '0.5rem' }}>
                            <strong>{audit.aprobada ? '✅ Aprobada' : '❌ Rechazada'}</strong>
                          </div>
                        )}
                        {audit.motivoRechazo && (
                          <div style={{ color: '#dc3545', marginBottom: '0.5rem' }}>
                            <strong>❌ Motivo rechazo:</strong> {audit.motivoRechazo}
                          </div>
                        )}
                        {audit.requiereRevision && (
                          <div style={{ color: '#ffc107', marginBottom: '0.5rem' }}>
                            <strong>⚠️ Requiere revisión adicional</strong>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    {user.role === 'admin' && (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {audit.estado === 'terminada' && !audit.aprobada && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const reason = prompt('Motivo para devolver para corrección:');
                                if (reason) {
                                  handleReturnForCorrection(audit.id, reason);
                                }
                              }}
                              style={{
                                background: '#ffc107',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              ↩️ Devolver para Corrección
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditMode(true);
                              }}
                              style={{
                                background: '#17a2b8',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              ✏️ Editar Auditoría
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal para Reasignar */}
      {showReassignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>🔄 Reasignar Auditoría #{showReassignModal}</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Operador:
              </label>
              <select
                id="reassign-operator"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              >
                <option value="">Sin asignar (devolver a pendientes)</option>
                {operators.map(op => (
                  <option key={op.id} value={op.id}>
                    {op.name} (@{op.username || op.email.split('@')[0]})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Prioridad:
              </label>
              <select
                id="reassign-priority"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
                defaultValue={audits.find(a => a.id === showReassignModal)?.prioridad || 'normal'}
              >
                <option value="normal">⚪ Normal</option>
                <option value="prioritaria">🟡 Prioritaria</option>
                <option value="urgente">🔴 Urgente</option>
              </select>
              <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                ℹ️ Cambiar prioridad no afecta el precio original para el operador
              </small>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Mensaje (opcional):
              </label>
              <textarea
                id="reassign-message"
                placeholder="Motivo de la reasignación..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowReassignModal(null)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const select = document.getElementById('reassign-operator') as HTMLSelectElement;
                  const textarea = document.getElementById('reassign-message') as HTMLTextAreaElement;
                  const prioritySelect = document.getElementById('reassign-priority') as HTMLSelectElement;
                  const operadorId = select.value ? parseInt(select.value) : undefined;
                  const mensaje = textarea.value.trim() || undefined;
                  const prioridad = prioritySelect.value;
                  handleReassignAudit(showReassignModal, operadorId, mensaje, prioridad);
                }}
                style={{
                  background: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🔄 Reasignar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Agregar Mensaje */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>💬 Agregar Mensaje - Auditoría #{showMessageModal}</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Tipo de mensaje:
              </label>
              <select
                id="message-type"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              >
                <option value="general">💬 Nota general</option>
                <option value="operador">👷 Mensaje para operador</option>
                <option value="supervisor">👨‍💼 Mensaje de supervisor</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Mensaje:
              </label>
              <textarea
                id="message-text"
                placeholder="Escribe tu mensaje aquí..."
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowMessageModal(null)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const select = document.getElementById('message-type') as HTMLSelectElement;
                  const textarea = document.getElementById('message-text') as HTMLTextAreaElement;
                  const tipo = select.value as 'operador' | 'supervisor' | 'general';
                  const mensaje = textarea.value.trim();
                  
                  if (mensaje) {
                    handleAddMessage(showMessageModal, mensaje, tipo);
                  }
                }}
                style={{
                  background: '#fd7e14',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                💬 Enviar Mensaje
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTracking;